/**
 * Documentation utilisateur.
 *
 * Public : les personnes qui LANCENT des simulations. Comment démarrer un
 * calcul, lire ce qu'il rend, comprendre un échec, savoir ce qu'il coûte.
 *
 * RÈGLE : cette page ne nomme jamais le moteur de calcul, ses bibliothèques ni
 * nos dépôts. Un utilisateur voit une plateforme MyMDO, pas notre chaîne
 * technique. Les seuls noms tiers admis sont ceux d'outils qu'il ouvrira
 * lui-même, comme un visualiseur de fichiers de champs.
 *
 * L'équipe simulation n'est PAS le public de cette page : sa documentation vit
 * dans le dépôt de calcul, avec les fichiers qu'elle décrit.
 */
import React, { useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  SchemaParcoursCase,
  SchemaCycleSimulation,
} from '../../components/docs/Schemas';

const Section = ({ children }) => (
  <div className="space-y-4 text-dark-200 leading-relaxed">{children}</div>
);

const Titre = ({ children }) => (
  <h2 className="text-xl font-semibold text-dark-100 mt-2">{children}</h2>
);

const SousTitre = ({ children }) => (
  <h3 className="text-base font-semibold text-dark-100 mt-6">{children}</h3>
);

const Tableau = ({ entetes, lignes }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm my-2">
      <thead>
        <tr className="border-b border-dark-700">
          {entetes.map((e) => (
            <th key={e} className="text-left py-2 pr-4 text-dark-400 font-medium">{e}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lignes.map((ligne, i) => (
          <tr key={i} className="border-b border-dark-800">
            {ligne.map((cellule, j) => (
              <td key={j} className={`py-2 pr-4 ${j === 0 ? 'text-dark-100' : 'text-dark-300'}`}>
                {cellule}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Encadre = ({ ton = 'info', titre, children }) => {
  const tons = {
    info: 'bg-primary-500/10 border-primary-500/30 text-primary-400',
    attention: 'bg-warning-500/10 border-warning-500/30 text-warning-400',
    danger: 'bg-error-500/10 border-error-500/30 text-error-400',
  };
  return (
    <div className={`border rounded-lg p-4 my-4 ${tons[ton]}`}>
      {titre && <p className="font-medium text-sm mb-1">{titre}</p>}
      <div className="text-dark-200 text-sm">{children}</div>
    </div>
  );
};

/**
 * Les sections sont construites à partir des dictionnaires plutôt que d'être
 * une constante : leur texte doit suivre le sélecteur de langue.
 *
 * <Trans> plutôt que t() là où un paragraphe contient du gras : il rend les
 * balises du dictionnaire en composants React, ce qui évite de découper une
 * phrase en trois clés pour mettre deux mots en évidence.
 */
const construireSections = (t) => [
  {
    id: 'demarrer',
    titre: t('doc.s1.titre'),
    contenu: (
      <Section>
        <Titre>{t('doc.s1.h1')}</Titre>
        <p><Trans i18nKey="doc.s1.p1" components={{ b: <strong /> }} /></p>
        <p><Trans i18nKey="doc.s1.p2" components={{ b: <strong /> }} /></p>
        <Encadre ton="info" titre={t('doc.s1.encadreTitre')}>
          {t('doc.s1.encadre')}
        </Encadre>
        <SousTitre>{t('doc.s1.h2')}</SousTitre>
        <p>{t('doc.s1.p3')}</p>
        <SchemaParcoursCase />
      </Section>
    ),
  },
  {
    id: 'suivre',
    titre: t('doc.s2.titre'),
    contenu: (
      <Section>
        <Titre>{t('doc.s2.h1')}</Titre>
        <SchemaCycleSimulation />
        <p><Trans i18nKey="doc.s2.p1" components={{ b: <strong /> }} /></p>
        <Tableau
          entetes={[t('doc.s2.statut'), t('doc.s2.signification')]}
          lignes={[
            ['queued', t('doc.s2.queued')],
            ['running', t('doc.s2.running')],
            ['completed', t('doc.s2.completed')],
            ['failed', t('doc.s2.failed')],
          ]}
        />
      </Section>
    ),
  },
  {
    id: 'resultats',
    titre: t('doc.s3.titre'),
    contenu: (
      <Section>
        <Titre>{t('doc.s3.h1')}</Titre>
        <Tableau
          entetes={[t('doc.s3.sortie'), t('doc.s3.cestQuoi')]}
          lignes={[
            ['CD', t('doc.s3.cd')],
            ['CL', t('doc.s3.cl')],
            [t('doc.s3.convergenceNom'), t('doc.s3.convergence')],
            ['STL', t('doc.s3.stl')],
            ['VTK', t('doc.s3.vtk')],
          ]}
        />
        <Encadre ton="attention" titre={t('doc.s3.encadreTitre')}>
          {t('doc.s3.encadre')}
        </Encadre>
      </Section>
    ),
  },
  {
    id: 'echecs',
    titre: t('doc.s4.titre'),
    contenu: (
      <Section>
        <Titre>{t('doc.s4.h1')}</Titre>
        <p><Trans i18nKey="doc.s4.p1" components={{ b: <strong /> }} /></p>
        <SousTitre>{t('doc.s4.h2')}</SousTitre>
        <Tableau
          entetes={[t('doc.s4.cePasse'), t('doc.s4.aFaire')]}
          lignes={[
            [t('doc.s4.memoire'), t('doc.s4.memoireAction')],
            [t('doc.s4.spot'), t('doc.s4.spotAction')],
            [t('doc.s4.solveur'), t('doc.s4.solveurAction')],
            [t('doc.s4.image'), t('doc.s4.imageAction')],
          ]}
        />
        <Encadre ton="danger" titre={t('doc.s4.encadreTitre')}>
          {t('doc.s4.encadre')}
        </Encadre>
      </Section>
    ),
  },
  {
    id: 'couts',
    titre: t('doc.s5.titre'),
    contenu: (
      <Section>
        <Titre>{t('doc.s5.h1')}</Titre>
        <Tableau
          entetes={[t('doc.s5.calcul'), t('doc.s5.duree'), t('doc.s5.cout')]}
          lignes={[
            [t('doc.s5.petit'), '5-10 min', '~0,01 $'],
            [t('doc.s5.gros'), '10-25 min', '~0,04 $'],
            [t('doc.s5.complet'), '1-4 h', '1-2 $'],
          ]}
        />
        <p><Trans i18nKey="doc.s5.p1" components={{ b: <strong /> }} /></p>
      </Section>
    ),
  },
];

const DocumentationPage = () => {
  const { t } = useTranslation();
  const sections = useMemo(() => construireSections(t), [t]);
  const [active, setActive] = useState('demarrer');
  const section = sections.find((s) => s.id === active) || sections[0];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-dark-100">{t('doc.titre')}</h1>
        <p className="text-dark-400 text-sm mt-1">
          {t('doc.sousTitre')}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <nav className="lg:w-56 shrink-0" aria-label={t('doc.nav')}>
          <ul className="space-y-1">
            {sections.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => setActive(s.id)}
                  aria-current={s.id === active ? 'page' : undefined}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    s.id === active
                      ? 'bg-primary-500/15 text-primary-400 font-medium'
                      : 'text-dark-300 hover:bg-dark-800 hover:text-dark-100'
                  }`}
                >
                  {s.titre}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <article className="card-dark p-6 flex-1 min-w-0">{section.contenu}</article>
      </div>
    </div>
  );
};

export default DocumentationPage;
